import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Zap } from "lucide-react";
import { electricityMarkerLabel } from "@/lib/fernausleseApi";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  badgeSizePx,
  clampPlanZoom,
  cornerMarkerFontSizePx,
  cornerMarkerSizePx,
  PLAN_ZOOM_STEP,
  pointsToSvg,
  polygonTopRightInset,
  polygonToRoundedPath,
  type MapPoint,
  type PitchHighlightRole,
  statusKindLabel,
  type SitePlanPitchOverlay,
  sitePlanPitchSurface,
} from "@/lib/sitePlan";
import { isSvgPlanImage, rasterizeSvgPlan } from "@/lib/sitePlanImage";

export type { SitePlanPitchOverlay };

interface SitePlanCanvasProps {
  imageData: string | null;
  pitches: SitePlanPitchOverlay[];
  badgeScale?: number;
  mode: "view" | "edit";
  selectedPitchId?: string | null;
  selectedPitchIds?: string[];
  pitchHighlights?: Record<string, PitchHighlightRole>;
  selectablePitchIds?: string[] | null;
  /** Ausgegraute Plätze – klickbar über onDimmedPitchSelect */
  dimmedPitchIds?: string[] | null;
  readOnly?: boolean;
  draftPoints?: MapPoint[];
  onMapClick?: (point: MapPoint) => void;
  onDraftPointMove?: (index: number, point: MapPoint) => void;
  onPitchSelect?: (pitchId: string) => void;
  onDimmedPitchSelect?: (pitchId: string) => void;
  className?: string;
  /** `fill` = maximale Höhe für Vollbild-Platzplan-Seite */
  viewportSize?: "default" | "fill";
}

interface SitePlanPitchPathProps {
  pitch: SitePlanPitchOverlay;
  selected: boolean;
  highlightRole: PitchHighlightRole;
  hoverable: boolean;
  interactive: boolean;
  isHovered: boolean;
  desktopHoverEnabled: boolean;
  onHover: (pitchId: string, clientX: number, clientY: number) => void;
  onHoverMove: (clientX: number, clientY: number) => void;
  onLeave: (pitchId: string) => void;
  onActivate: (pitchId: string) => void;
}

const SitePlanPitchPath = memo(function SitePlanPitchPath({
  pitch,
  selected,
  highlightRole,
  hoverable,
  interactive,
  isHovered,
  desktopHoverEnabled,
  onHover,
  onHoverMove,
  onLeave,
  onActivate,
}: SitePlanPitchPathProps) {
  const points = pitch.map_polygon!;
  const pathD = useMemo(() => polygonToRoundedPath(points), [points]);
  const surface = useMemo(
    () =>
      sitePlanPitchSurface(
        pitch.status_kind ?? "free",
        pitch.booking_status,
        pitch.transition_hint,
        { selected, highlightRole },
      ),
    [pitch.status_kind, pitch.booking_status, pitch.transition_hint, selected, highlightRole],
  );

  return (
    <g
      data-pitch-overlay
      className={clsx(
        hoverable && "site-plan-pitch--hoverable",
        interactive && "site-plan-pitch--interactive",
        isHovered && "site-plan-pitch--hovered",
      )}
    >
      <path
        d={pathD}
        className={clsx("site-plan-pitch", "site-plan-pitch__backing", surface.surfaceClass)}
        aria-hidden
      />
      <path
        d={pathD}
        className={clsx("site-plan-pitch", "site-plan-pitch__surface", surface.surfaceClass)}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? `Stellplatz ${pitch.code}` : undefined}
        onPointerEnter={(e) => {
          if (!hoverable || !desktopHoverEnabled) return;
          onHover(pitch.id, e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (!hoverable || !desktopHoverEnabled) return;
          onHoverMove(e.clientX, e.clientY);
        }}
        onPointerLeave={() => {
          onLeave(pitch.id);
        }}
        onPointerDown={(e) => {
          if (!interactive || e.button !== 0) return;
          e.stopPropagation();
        }}
        onClick={(e) => {
          if (!interactive) return;
          e.stopPropagation();
          onActivate(pitch.id);
        }}
        onKeyDown={(e) => {
          if (!interactive) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate(pitch.id);
          }
        }}
      />
    </g>
  );
});

function clientToNormalized(
  mapRect: DOMRect,
  clientX: number,
  clientY: number,
): MapPoint {
  if (mapRect.width <= 0 || mapRect.height <= 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: Math.min(1, Math.max(0, (clientX - mapRect.left) / mapRect.width)),
    y: Math.min(1, Math.max(0, (clientY - mapRect.top) / mapRect.height)),
  };
}

/** SVG viewBox 0..1 – Strichstärken in Plan-Koordinaten (nicht Pixel). */
/** Nach diesem Verweilen klappt der Hinweis auf und zeigt die weiteren Angaben. */
const HOVER_DETAIL_DELAY_MS = 700;

function pitchHasHoverDetails(pitch: SitePlanPitchOverlay): boolean {
  return Boolean(
    (pitch.label && pitch.label !== pitch.code) ||
      pitch.guest_name ||
      pitch.booking_number ||
      pitch.booking_status ||
      pitch.electricity_status ||
      (pitch.reasons && pitch.reasons.length > 0),
  );
}

const DRAFT_STROKE = 0.004;
const DRAFT_FILL = "color-mix(in srgb, var(--eh-accent) 18%, transparent)";
function stageTransform(zoom: number, pan: { x: number; y: number }): string | undefined {
  if (zoom === 1 && pan.x === 0 && pan.y === 0) return undefined;
  return `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
}

/**
 * SVG-Hintergründe einmal in ein PNG umwandeln. Rasterbilder bleiben unverändert.
 * `src` ist null, solange die Aufbereitung läuft.
 */
function useDisplayPlanImage(imageData: string | null): { src: string | null; preparing: boolean } {
  const [raster, setRaster] = useState<{ source: string; url: string } | null>(null);
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const rasterUrlRef = useRef<string | null>(null);
  const svg = imageData != null && isSvgPlanImage(imageData);

  useEffect(() => {
    return () => {
      if (rasterUrlRef.current) URL.revokeObjectURL(rasterUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!imageData || !isSvgPlanImage(imageData)) return;
    let cancelled = false;
    rasterizeSvgPlan(imageData)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (rasterUrlRef.current) URL.revokeObjectURL(rasterUrlRef.current);
        rasterUrlRef.current = url;
        setRaster({ source: imageData, url });
      })
      .catch(() => {
        if (!cancelled) setFailedSource(imageData);
      });
    return () => {
      cancelled = true;
    };
  }, [imageData]);

  if (!imageData) return { src: null, preparing: false };
  if (!svg) return { src: imageData, preparing: false };
  if (failedSource === imageData) return { src: imageData, preparing: false };
  if (raster?.source === imageData) return { src: raster.url, preparing: false };
  return { src: null, preparing: true };
}

function PitchHoverTooltip({
  tipRef,
  pitch,
  statusLabel,
  expanded,
}: {
  tipRef: React.Ref<HTMLDivElement>;
  pitch: SitePlanPitchOverlay;
  statusLabel: string | null;
  expanded: boolean;
}) {
  const surface = sitePlanPitchSurface(
    pitch.status_kind ?? "free",
    pitch.booking_status,
    pitch.transition_hint,
  );
  const label =
    statusLabel ?? statusKindLabel(pitch.status_kind ?? "free", pitch.transition_hint);
  return (
    <div
      ref={tipRef}
      className={clsx(
        "site-plan-pitch-tooltip pointer-events-none",
        expanded && "site-plan-pitch-tooltip--expanded",
      )}
      aria-hidden
    >
      <span className="site-plan-pitch-tooltip__code">{pitch.code}</span>
      <span className={clsx("site-plan-pitch-badge", `site-plan-pitch-badge--${surface.accent}`)}>
        {label}
      </span>
      {expanded ? (
        <dl className="site-plan-pitch-tooltip__details">
          {pitch.label && pitch.label !== pitch.code ? (
            <div>
              <dt>Platz</dt>
              <dd>{pitch.label}</dd>
            </div>
          ) : null}
          {pitch.guest_name ? (
            <div>
              <dt>Gast</dt>
              <dd>{pitch.guest_name}</dd>
            </div>
          ) : null}
          {pitch.booking_number || pitch.booking_status ? (
            <div>
              <dt>Buchung</dt>
              <dd className="flex flex-col items-end gap-1">
                {pitch.booking_number ? <span>{pitch.booking_number}</span> : null}
                {pitch.booking_status ? <StatusBadge status={pitch.booking_status} /> : null}
              </dd>
            </div>
          ) : null}
          {pitch.electricity_status ? (
            <div>
              <dt>Strom</dt>
              <dd>{electricityMarkerLabel(pitch.electricity_status)}</dd>
            </div>
          ) : null}
          {pitch.reasons?.map((reason) => (
            <div key={reason}>
              <dt>Hinweis</dt>
              <dd>{reason}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

export const SitePlanCanvas = memo(function SitePlanCanvas({
  imageData,
  pitches,
  badgeScale = 1,
  mode,
  selectedPitchId,
  selectedPitchIds = [],
  pitchHighlights = {},
  selectablePitchIds = null,
  dimmedPitchIds = null,
  readOnly = false,
  draftPoints = [],
  onMapClick,
  onDraftPointMove,
  onPitchSelect,
  onDimmedPitchSelect,
  className,
  viewportSize = "default",
}: SitePlanCanvasProps) {
  const mapLayerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const { src: displayImage, preparing: preparingImage } = useDisplayPlanImage(imageData);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [gestureActive, setGestureActive] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageRatio, setImageRatio] = useState<number | null>(null);
  const [confirmedHoveredPitchId, setConfirmedHoveredPitchId] = useState<string | null>(null);
  const [expandedHover, setExpandedHover] = useState(false);
  const [desktopHoverEnabled, setDesktopHoverEnabled] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipClientRef = useRef<{ x: number; y: number } | null>(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const panStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const pinchStart = useRef<{
    distance: number;
    zoom: number;
    panX: number;
    panY: number;
    focalX: number;
    focalY: number;
    centerX: number;
    centerY: number;
  } | null>(null);
  const draggingPointIndex = useRef<number | null>(null);
  const cornerPx = cornerMarkerSizePx(badgeSizePx(badgeScale));
  const cornerFontPx = cornerMarkerFontSizePx(cornerPx);

  zoomRef.current = zoom;
  panRef.current = pan;

  useEffect(() => {
    setImageRatio(null);
    setImageError(false);
  }, [imageData]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (mode === "edit" || pinchStart.current) return;
      const target = e.target as Element;
      if (target.closest("[data-pitch-overlay]")) return;
      setGestureActive(true);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      panStart.current = {
        x: e.clientX,
        y: e.clientY,
        panX: panRef.current.x,
        panY: panRef.current.y,
      };
    },
    [mode],
  );

  const paintStage = useCallback((nextZoom: number, nextPan: { x: number; y: number }) => {
    zoomRef.current = nextZoom;
    panRef.current = nextPan;
    const el = stageRef.current;
    if (!el) return;
    el.style.transform = stageTransform(nextZoom, nextPan) ?? "";
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!panStart.current || pinchStart.current) return;
      paintStage(zoomRef.current, {
        x: panStart.current.panX + (e.clientX - panStart.current.x),
        y: panStart.current.panY + (e.clientY - panStart.current.y),
      });
    },
    [paintStage],
  );

  const finishGesture = useCallback(() => {
    setZoom(zoomRef.current);
    setPan({ x: panRef.current.x, y: panRef.current.y });
    requestAnimationFrame(() => {
      if (!panStart.current && !pinchStart.current) setGestureActive(false);
    });
  }, []);

  const endPanGesture = useCallback(() => {
    panStart.current = null;
    if (!pinchStart.current) finishGesture();
  }, [finishGesture]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mode === "edit") return;

    const pinchMetrics = (touches: TouchList) => {
      const a = touches[0];
      const b = touches[1];
      if (!a || !b) return null;
      const rect = el.getBoundingClientRect();
      return {
        distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        focalX: (a.clientX + b.clientX) / 2,
        focalY: (a.clientY + b.clientY) / 2,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
      };
    };

    const applyPinchZoom = (touches: TouchList) => {
      const start = pinchStart.current;
      const metrics = pinchMetrics(touches);
      if (!start || !metrics || metrics.distance <= 0 || start.distance <= 0) return;

      const nextZoom = clampPlanZoom((metrics.distance / start.distance) * start.zoom);
      const focalOffsetX = start.focalX - start.centerX;
      const focalOffsetY = start.focalY - start.centerY;
      const worldX = (focalOffsetX - start.panX) / start.zoom;
      const worldY = (focalOffsetY - start.panY) / start.zoom;
      const nextPan = {
        x: metrics.focalX - metrics.centerX - worldX * nextZoom,
        y: metrics.focalY - metrics.centerY - worldY * nextZoom,
      };

      paintStage(nextZoom, nextPan);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const metrics = pinchMetrics(e.touches);
        if (!metrics || metrics.distance <= 0) return;
        panStart.current = null;
        setGestureActive(true);
        pinchStart.current = {
          distance: metrics.distance,
          zoom: zoomRef.current,
          panX: panRef.current.x,
          panY: panRef.current.y,
          focalX: metrics.focalX,
          focalY: metrics.focalY,
          centerX: metrics.centerX,
          centerY: metrics.centerY,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !pinchStart.current) return;
      e.preventDefault();
      applyPinchZoom(e.touches);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinchStart.current = null;
        panStart.current = null;
      }
      if (e.touches.length === 0) finishGesture();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [mode, finishGesture, paintStage]);

  const placeDraftPoint = useCallback(
    (clientX: number, clientY: number) => {
      if (mode !== "edit" || !onMapClick || !mapLayerRef.current) return;
      const rect = mapLayerRef.current.getBoundingClientRect();
      onMapClick(clientToNormalized(rect, clientX, clientY));
    },
    [mode, onMapClick],
  );

  const moveDraftPoint = useCallback(
    (index: number, clientX: number, clientY: number) => {
      if (mode !== "edit" || !onDraftPointMove || !mapLayerRef.current) return;
      const rect = mapLayerRef.current.getBoundingClientRect();
      onDraftPointMove(index, clientToNormalized(rect, clientX, clientY));
    },
    [mode, onDraftPointMove],
  );

  const handleSvgPointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (mode !== "edit" || !onMapClick || e.button !== 0) return;
      if (draggingPointIndex.current !== null) return;
      if (e.altKey) return;
      const target = e.target as Element;
      if (target.closest("[data-pitch-overlay]")) return;
      if (target.closest("[data-draft-point]")) return;
      e.preventDefault();
      placeDraftPoint(e.clientX, e.clientY);
    },
    [mode, onMapClick, placeDraftPoint],
  );

  useEffect(() => {
    setImageError(false);
  }, [imageData]);

  const resetHoverState = useCallback(() => {
    tooltipClientRef.current = null;
    setConfirmedHoveredPitchId(null);
    setExpandedHover(false);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => {
      setDesktopHoverEnabled(mq.matches);
      if (!mq.matches) resetHoverState();
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [resetHoverState]);

  useEffect(() => {
    if (gestureActive) resetHoverState();
  }, [gestureActive, resetHoverState]);

  const selectedSet = useMemo(() => {
    const ids = new Set(selectedPitchIds);
    if (selectedPitchId) ids.add(selectedPitchId);
    return ids;
  }, [selectedPitchId, selectedPitchIds]);

  const selectableSet = useMemo(
    () => (selectablePitchIds ? new Set(selectablePitchIds) : null),
    [selectablePitchIds],
  );

  const dimmedSet = useMemo(
    () => (dimmedPitchIds ? new Set(dimmedPitchIds) : null),
    [dimmedPitchIds],
  );

  const overlays = useMemo(
    () =>
      pitches.filter(
        (p) =>
          p.map_polygon &&
          p.map_polygon.length >= 3 &&
          !(mode === "edit" && p.id === selectedPitchId),
      ),
    [pitches, mode, selectedPitchId],
  );

  useEffect(() => {
    setExpandedHover(false);
    if (!confirmedHoveredPitchId) return;
    const pitch = overlays.find((item) => item.id === confirmedHoveredPitchId);
    if (!pitch || !pitchHasHoverDetails(pitch)) return;
    const timer = window.setTimeout(() => setExpandedHover(true), HOVER_DETAIL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [confirmedHoveredPitchId, overlays]);

  const hoveredPitch = useMemo(
    () =>
      confirmedHoveredPitchId
        ? (overlays.find((p) => p.id === confirmedHoveredPitchId) ?? null)
        : null,
    [confirmedHoveredPitchId, overlays],
  );

  const placeTooltip = useCallback((clientX: number, clientY: number) => {
    tooltipClientRef.current = { x: clientX, y: clientY };
    const tip = tooltipRef.current;
    if (!tip) return;
    tip.style.left = `${clientX}px`;
    tip.style.top = `${clientY}px`;
  }, []);

  useLayoutEffect(() => {
    const point = tooltipClientRef.current;
    if (!point || !confirmedHoveredPitchId) return;
    placeTooltip(point.x, point.y);
  }, [confirmedHoveredPitchId, placeTooltip]);

  const handlePitchActivate = useCallback(
    (pitchId: string) => {
      if (mode === "edit" || readOnly) return;
      if (selectableSet?.has(pitchId)) {
        onPitchSelect?.(pitchId);
        return;
      }
      if (dimmedSet?.has(pitchId)) {
        onDimmedPitchSelect?.(pitchId);
        return;
      }
      if (!selectableSet && !dimmedSet) {
        onPitchSelect?.(pitchId);
      }
    },
    [mode, readOnly, selectableSet, dimmedSet, onPitchSelect, onDimmedPitchSelect],
  );

  const handlePitchHover = useCallback(
    (pitchId: string, clientX: number, clientY: number) => {
      placeTooltip(clientX, clientY);
      setConfirmedHoveredPitchId((current) => (current === pitchId ? current : pitchId));
    },
    [placeTooltip],
  );

  const handlePitchLeave = useCallback((pitchId: string) => {
    setConfirmedHoveredPitchId((current) => (current === pitchId ? null : current));
  }, []);

  const resetView = useCallback(() => {
    paintStage(1, { x: 0, y: 0 });
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [paintStage]);

  const showDesktopPitchTooltip = mode === "view" && desktopHoverEnabled && hoveredPitch;

  const hoveredTooltipStatus = hoveredPitch
    ? (hoveredPitch.status_label ??
      statusKindLabel(hoveredPitch.status_kind ?? "free", hoveredPitch.transition_hint))
    : null;

  if (!imageData) {
    return (
      <div
        className={clsx(
          "flex min-h-[240px] items-center justify-center rounded-xl border border-dashed border-separator bg-fill-tertiary/30 p-6 text-center text-sm text-label-secondary",
          className,
        )}
      >
        Noch kein Platzplan hinterlegt. Laden Sie in den Stammdaten unter „Platzplan“ ein Bild
        hoch.
      </div>
    );
  }

  const draftPointSvg = pointsToSvg(draftPoints);
  const draftEdges =
    draftPoints.length >= 2 ? (
      <polyline
        points={draftPointSvg}
        fill="none"
        stroke="var(--eh-accent)"
        strokeWidth={DRAFT_STROKE}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    ) : null;
  const draftPolygon =
    draftPoints.length >= 3 ? (
      <polygon
        points={draftPointSvg}
        fill={DRAFT_FILL}
        stroke="var(--eh-accent)"
        strokeWidth={DRAFT_STROKE}
        vectorEffect="non-scaling-stroke"
      />
    ) : null;

  return (
    <div
      className={clsx(
        viewportSize === "fill"
          ? "site-plan-canvas site-plan-canvas--fill flex min-h-0 flex-1 flex-col gap-1.5"
          : "space-y-2",
        className,
      )}
    >
      <div
        className={clsx(
          "flex shrink-0 flex-wrap items-center",
          viewportSize === "fill" ? "gap-1.5" : "gap-2",
        )}
      >
        <button
          type="button"
          className={clsx(
            "eh-btn eh-btn--secondary px-3 text-sm",
            viewportSize === "fill" ? "eh-btn--sm h-8 min-h-8 min-w-8" : "min-h-[44px] min-w-[44px]",
          )}
          onClick={() => {
            const next = clampPlanZoom(zoomRef.current + PLAN_ZOOM_STEP);
            zoomRef.current = next;
            setZoom(next);
          }}
          aria-label="Vergrößern"
        >
          +
        </button>
        <button
          type="button"
          className={clsx(
            "eh-btn eh-btn--secondary px-3 text-sm",
            viewportSize === "fill" ? "eh-btn--sm h-8 min-h-8 min-w-8" : "min-h-[44px] min-w-[44px]",
          )}
          onClick={() => {
            const next = clampPlanZoom(zoomRef.current - PLAN_ZOOM_STEP);
            zoomRef.current = next;
            setZoom(next);
          }}
          aria-label="Verkleinern"
        >
          −
        </button>
        <button
          type="button"
          className={clsx(
            "eh-btn eh-btn--secondary px-3 text-sm",
            viewportSize === "fill" ? "eh-btn--sm h-8 min-h-8" : "min-h-[44px]",
          )}
          onClick={resetView}
        >
          Ansicht zurücksetzen
        </button>
        {mode === "edit" ? (
          <span className="min-w-0 flex-1 truncate text-xs text-label-secondary sm:text-sm">
            Tippen oder klicken Sie auf den Plan, um Eckpunkte zu setzen (mindestens drei).
            Bestehende Punkte können Sie per Ziehen verschieben. Zoomen über +/−.
          </span>
        ) : (
          <span className="min-w-0 flex-1 truncate text-xs text-label-secondary">
            Tippen Sie einen Platz an. Zwei Finger zum Zoomen, ziehen zum Verschieben.
          </span>
        )}
      </div>

      <div
        ref={containerRef}
        className={clsx(
          "site-plan-canvas__viewport relative overflow-hidden border border-separator",
          viewportSize === "fill"
            ? "site-plan-canvas__viewport--fill min-h-0 flex-1"
            : "max-h-[min(72dvh,720px)] min-h-[280px]",
          mode === "view" ? "touch-none" : "touch-pan-y",
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPanGesture}
        onPointerCancel={endPanGesture}
      >
        <div className={viewportSize === "fill" ? "site-plan-canvas__fit-root" : undefined}>
        <div
          ref={stageRef}
          className={clsx(
            "site-plan-canvas__stage origin-center",
            viewportSize === "fill" && "site-plan-canvas__stage--fill",
            !gestureActive && "transition-transform duration-75",
          )}
          style={
            gestureActive
              ? { transform: stageTransform(zoomRef.current, panRef.current) }
              : stageTransform(zoom, pan)
                ? { transform: stageTransform(zoom, pan) }
                : undefined
          }
        >
          <div
            ref={mapLayerRef}
            className={clsx(
              "relative select-none",
              viewportSize === "fill" && imageRatio ? "site-plan-map-fit" : "w-full",
            )}
            style={
              viewportSize === "fill" && imageRatio
                ? ({ "--plan-ratio": imageRatio } as React.CSSProperties)
                : undefined
            }
          >
            {displayImage ? (
              <img
                src={displayImage}
                alt="Platzplan"
                className={clsx(
                  "block max-w-full",
                  viewportSize === "fill" && imageRatio ? "h-full w-full" : "h-auto w-full",
                )}
                draggable={false}
                decoding="async"
                onError={() => setImageError(true)}
                onLoad={(e) => {
                  setImageError(false);
                  const { naturalWidth, naturalHeight } = e.currentTarget;
                  if (naturalWidth > 0 && naturalHeight > 0) {
                    setImageRatio(naturalWidth / naturalHeight);
                  }
                }}
              />
            ) : (
              <div className="flex min-h-[240px] items-center justify-center p-6 text-center text-sm text-label-secondary">
                {preparingImage ? "Platzplan wird aufbereitet…" : "Platzplan wird geladen…"}
              </div>
            )}
            {imageError ? (
              <div className="absolute inset-0 flex items-center justify-center bg-fill-tertiary/80 p-4 text-center text-sm text-status-red">
                Platzplan-Bild konnte nicht angezeigt werden. Bitte in den Stammdaten erneut
                hochladen.
              </div>
            ) : null}
            {displayImage ? (
            <>
            <svg
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              className={clsx(
                "absolute inset-0 h-full w-full",
                mode === "edit" ? "cursor-crosshair" : "",
              )}
              role="img"
              aria-label="Platzplan mit Stellplatzflächen"
              onPointerUp={handleSvgPointerUp}
            >
              {overlays.map((pitch) => {
                const selected = selectedSet.has(pitch.id);
                const highlightRole = pitchHighlights[pitch.id] ?? (selected ? "assigned" : "none");
                const hoverable = mode === "view";
                const interactive =
                  hoverable &&
                  !readOnly &&
                  ((!selectableSet && !dimmedSet) ||
                    Boolean(selectableSet?.has(pitch.id)) ||
                    Boolean(dimmedSet?.has(pitch.id)));
                return (
                  <SitePlanPitchPath
                    key={pitch.id}
                    pitch={pitch}
                    selected={selected}
                    highlightRole={highlightRole}
                    hoverable={hoverable}
                    interactive={interactive}
                    isHovered={confirmedHoveredPitchId === pitch.id}
                    desktopHoverEnabled={desktopHoverEnabled}
                    onHover={handlePitchHover}
                    onHoverMove={placeTooltip}
                    onLeave={handlePitchLeave}
                    onActivate={handlePitchActivate}
                  />
                );
              })}
              {selectedPitchId && mode === "edit" ? draftEdges : null}
              {selectedPitchId && mode === "edit" ? draftPolygon : null}
            </svg>

            {pitches.map((pitch) => {
              if (!pitch.electricity_status || !pitch.map_polygon || pitch.map_polygon.length < 3) {
                return null;
              }
              const corner = polygonTopRightInset(pitch.map_polygon);
              const label = electricityMarkerLabel(pitch.electricity_status);
              return (
                <span
                  key={`power-${pitch.id}`}
                  className={`site-plan-power site-plan-power--${pitch.electricity_status}`}
                  style={{ left: `${corner.x * 100}%`, top: `${corner.y * 100}%` }}
                  title={`${pitch.code}: ${label}`}
                  aria-label={`${pitch.code}: ${label}`}
                >
                  <Zap aria-hidden strokeWidth={2.4} fill="currentColor" />
                </span>
              );
            })}

            {selectedPitchId && mode === "edit"
              ? draftPoints.map((pt, idx) => (
                  <div
                    key={`draft-marker-${idx}`}
                    data-draft-point
                    className="absolute z-20 flex -translate-x-1/2 -translate-y-1/2 touch-none items-center justify-center"
                    style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%` }}
                    onPointerDown={(e) => {
                      if (mode !== "edit" || !onDraftPointMove || e.button !== 0) return;
                      e.stopPropagation();
                      e.preventDefault();
                      draggingPointIndex.current = idx;
                      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                      if (draggingPointIndex.current !== idx) return;
                      moveDraftPoint(idx, e.clientX, e.clientY);
                    }}
                    onPointerUp={(e) => {
                      if (draggingPointIndex.current !== idx) return;
                      draggingPointIndex.current = null;
                      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                    }}
                    onPointerCancel={(e) => {
                      if (draggingPointIndex.current !== idx) return;
                      draggingPointIndex.current = null;
                      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                    }}
                    aria-label={`Eckpunkt ${idx + 1} verschieben`}
                  >
                    <span
                      className="flex cursor-grab items-center justify-center rounded-full border-white bg-accent font-bold text-white shadow-lg ring-accent/40 active:cursor-grabbing"
                      style={{
                        width: cornerPx,
                        height: cornerPx,
                        fontSize: cornerFontPx,
                        borderWidth: Math.max(1, Math.round(cornerPx * 0.1)),
                        boxShadow: `0 0 0 ${Math.max(1, Math.round(cornerPx * 0.1))}px color-mix(in srgb, var(--eh-accent) 40%, transparent)`,
                      }}
                    >
                      {idx + 1}
                    </span>
                  </div>
                ))
              : null}
            </>
            ) : null}
          </div>
        </div>
        </div>
      </div>
      {showDesktopPitchTooltip && hoveredPitch ? (
        <PitchHoverTooltip
          tipRef={tooltipRef}
          pitch={hoveredPitch}
          statusLabel={hoveredTooltipStatus}
          expanded={expandedHover && pitchHasHoverDetails(hoveredPitch)}
        />
      ) : null}
    </div>
  );
});
